import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  HStack,
  IconButton,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
} from '@chakra-ui/react';

import DevSandboxTooltip from './DevSandboxTooltip';
import { getEntityFeatures } from './entityConfig';
import {
  IEditorSettings,
  TCSSMode,
  TDevSandboxMode,
  TEditorFramework,
} from './types';
import useSandboxTheme from './useSandboxTheme';

// Settings gear icon
const SettingsIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

interface ISettingsButtonProps {
  settings: IEditorSettings;
  onSettingsChange: (partial: Partial<IEditorSettings>) => void;
  onReset: () => void;
  /** 'icon' = just icon button (default), 'topbar' = icon + text button for topbar */
  variant?: 'icon' | 'topbar';
  /** Editor mode — hides Framework/CSS/Packages for practice problems */
  mode?: TDevSandboxMode;
}

const SectionLabel = ({ children, color = 'whiteAlpha.600' }: { children: React.ReactNode; color?: string }) => (
  <Text
    fontSize="micro"
    color={color}
    fontWeight="semibold"
    letterSpacing="0.05em"
    textTransform="uppercase"
    mb={2}
  >
    {children}
  </Text>
);

const SettingRow = ({ label, children, labelColor = 'whiteAlpha.900' }: { label: string; children: React.ReactNode; labelColor?: string }) => (
  <HStack justify="space-between" w="100%">
    <Text fontSize="small" color={labelColor}>
      {label}
    </Text>
    {children}
  </HStack>
);

const SettingsButton = ({ settings, onSettingsChange, onReset, variant = 'icon', mode }: ISettingsButtonProps) => {
  const { colors, isLight } = useSandboxTheme();
  const features = getEntityFeatures(mode);
  const allFrameworkOptions: { value: TEditorFramework; label: string }[] = [
    { value: 'vanilla', label: 'JS' },
    { value: 'react', label: 'React' },
    { value: 'vue', label: 'Vue' },
    { value: 'typescript', label: 'TS' },
  ];
  const frameworkOptions = features.jsOnlyFramework
    ? allFrameworkOptions.filter((o) => o.value === 'vanilla' || o.value === 'typescript')
    : allFrameworkOptions;

  const cssModeOptions: { value: TCSSMode; label: string }[] = [
    { value: 'css', label: 'CSS' },
    { value: 'scss', label: 'SCSS' },
    { value: 'postcss', label: 'PostCSS' },
    { value: 'modules', label: 'Modules' },
  ];

  const renderTriggerButton = () => {
    if (variant === 'topbar') {
      return (
        <DevSandboxTooltip label="Code editor settings">
          <span>
            <PopoverTrigger>
              <Button
                aria-label="Editor settings"
                leftIcon={<SettingsIcon size={18} />}
                size="md"
                variant="outline"
                borderColor={colors.controlBorder}
                color={colors.textSecondary}
                bg={isLight ? 'gray.50' : 'whiteAlpha.50'}
                _hover={{ color: colors.textPrimary, bg: colors.hoverBg, borderColor: colors.controlBorderHover }}
                _active={{ bg: colors.activeBg }}
                fontWeight="500"
                px={4}
              >
                Settings
              </Button>
            </PopoverTrigger>
          </span>
        </DevSandboxTooltip>
      );
    }

    return (
      <DevSandboxTooltip label="Code editor settings">
        <span>
          <PopoverTrigger>
            <IconButton
              aria-label="Editor settings"
              icon={<SettingsIcon />}
              size="sm"
              variant="ghost"
              color={colors.textSecondary}
              _hover={{ color: colors.textPrimary, bg: colors.hoverBg }}
              _active={{ bg: colors.activeBg }}
            />
          </PopoverTrigger>
        </span>
      </DevSandboxTooltip>
    );
  };

  // Shared button style helper for segmented controls
  const segmentedBtn = (isActive: boolean) => ({
    bg: isActive ? 'purple.500' : 'transparent',
    borderColor: isActive ? 'purple.500' : colors.controlBorder,
    color: isActive ? 'white' : colors.textSecondary,
    _hover: {
      bg: isActive ? 'purple.400' : colors.hoverBg,
    },
  });

  return (
    <Popover placement="bottom-end" trigger="click">
      {renderTriggerButton()}
      <PopoverContent
        bg={colors.popoverBg}
        borderColor={colors.popoverBorder}
        w="320px"
        boxShadow={colors.popoverShadow}
        _focus={{ outline: 'none', boxShadow: colors.popoverShadow }}
      >
        <PopoverArrow bg={colors.popoverBg} boxShadow="none" />
        <PopoverBody py={4} px={4}>
          <VStack align="stretch" spacing={4}>
            {/* Framework Section */}
            {features.showFramework && (
              <Box>
                <SectionLabel color={colors.sectionLabel}>{features.jsOnlyFramework ? 'Language' : 'Framework'}</SectionLabel>
                <ButtonGroup size="sm" isAttached variant="outline" w="100%">
                  {frameworkOptions.map((option) => (
                    <Button
                      key={option.value}
                      flex={1}
                      fontSize="small"
                      onClick={() => onSettingsChange({ framework: option.value })}
                      {...segmentedBtn(settings.framework === option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>
            )}

            {/* CSS Mode Section */}
            {features.showCssMode && (
              <Box>
                <SectionLabel color={colors.sectionLabel}>CSS</SectionLabel>
                <ButtonGroup size="sm" isAttached variant="outline" w="100%">
                  {cssModeOptions.map((option) => (
                    <Button
                      key={option.value}
                      flex={1}
                      fontSize="small"
                      onClick={() => onSettingsChange({ cssMode: option.value })}
                      {...segmentedBtn(settings.cssMode === option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>
            )}

            {(features.showFramework || features.showCssMode) && <Divider borderColor={colors.editorDivider} />}

            {/* Editor Section */}
            <Box>
              <SectionLabel color={colors.sectionLabel}>Editor</SectionLabel>
              <VStack align="stretch" spacing={4}>
                {/* Font Size */}
                <SettingRow label="Font Size" labelColor={colors.textPrimary}>
                  <NumberInput
                    size="sm"
                    w="90px"
                    min={12}
                    max={20}
                    value={settings.fontSize}
                    onChange={(_, value) => {
                      if (!isNaN(value) && value >= 12 && value <= 20) {
                        onSettingsChange({ fontSize: value });
                      }
                    }}
                  >
                    <NumberInputField
                      bg={colors.controlBg}
                      borderColor={colors.controlBorder}
                      color={colors.textPrimary}
                      _hover={{ borderColor: colors.controlBorderHover }}
                      textAlign="center"
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper borderColor={colors.controlBorder} color={colors.textSecondary} />
                      <NumberDecrementStepper borderColor={colors.controlBorder} color={colors.textSecondary} />
                    </NumberInputStepper>
                  </NumberInput>
                </SettingRow>

                {/* Tab Size */}
                <SettingRow label="Tab Size" labelColor={colors.textPrimary}>
                  <ButtonGroup size="sm" isAttached variant="outline">
                    <Button
                      onClick={() => onSettingsChange({ tabSize: 2 })}
                      {...segmentedBtn(settings.tabSize === 2)}
                    >
                      2
                    </Button>
                    <Button
                      onClick={() => onSettingsChange({ tabSize: 4 })}
                      {...segmentedBtn(settings.tabSize === 4)}
                    >
                      4
                    </Button>
                  </ButtonGroup>
                </SettingRow>

                {/* Line Numbers */}
                <Checkbox
                  size="md"
                  isChecked={settings.lineNumbers}
                  onChange={(e) => onSettingsChange({ lineNumbers: e.target.checked })}
                  colorScheme="purple"
                >
                  <Text fontSize="small" color={colors.textPrimary}>Line Numbers</Text>
                </Checkbox>

                {/* Word Wrap */}
                <Checkbox
                  size="md"
                  isChecked={settings.wordWrap}
                  onChange={(e) => onSettingsChange({ wordWrap: e.target.checked })}
                  colorScheme="purple"
                >
                  <Text fontSize="small" color={colors.textPrimary}>Word Wrap</Text>
                </Checkbox>

                {/* Auto Format */}
                <Checkbox
                  size="md"
                  isChecked={settings.autoFormat}
                  onChange={(e) => onSettingsChange({ autoFormat: e.target.checked })}
                  colorScheme="purple"
                >
                  <Text fontSize="small" color={colors.textPrimary}>Format on Save</Text>
                </Checkbox>
              </VStack>
            </Box>

            <Divider borderColor={colors.editorDivider} />

            {/* Reset Button */}
            <Button
              size="sm"
              variant="ghost"
              color={colors.textTertiary}
              fontSize="small"
              _hover={{ color: colors.textPrimary, bg: colors.hoverBg }}
              onClick={onReset}
            >
              Reset to Defaults
            </Button>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default SettingsButton;
